import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Descriptions,
  Divider,
  Skeleton,
  Typography,
  message as _message,
  Popconfirm
} from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { ContractIcon, AmendmentIcon } from '../../utils/constants/customIcon';
import useAuthContext from '../../contexts/AuthContext';
import PageHeader from '../../components/PageHeader/PageHeader';
import useErrorMessage from '../../utils/ErrorMessage';
import ContentCustom from '../../components/ContentCustom/ContentCustom';
import MissingDocUpload from './MissingDocUpload';
import {
  routes,
  subRoutes,
  outOfNavRoutes
} from '../../utils/constants/routes';

const ShowMissingDocuments = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [contract, setContract] = useState();
  const { dispatchAPI, user } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const { message } = useErrorMessage();
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();
  const customerView = user?.role === 'guests:GUEST';

  const getContract = async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/contracts/${id}?populate=insurer,documents.files,rib,kBis,infoStatement,certificateNonClaim,customerCni,cniRecipient,bail,photos`
      });
      setContract(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      await getContract();
    })();
    // eslint-disable-next-line
  }, [refresh]);

  const forceRefresh = () => {
    setRefresh(!refresh);
  };

  const validateDocs = async (required) => {
    let isOk = false;
    for (let i = 0; i < contract.documents.length; i += 1) {
      if (contract.documents[i].required === required) {
        if (contract.documents[i].status === 'SAVED') {
          isOk = true;
          break;
        }
      }
    }
    if (!isOk) {
      _message.success(`Vous n'avez enregistré aucun document`);
    } else {
      try {
        for (let i = 0; i < contract.documents.length; i += 1) {
          if (contract.documents[i].status === 'SAVED') {
            await dispatchAPI('POST', {
              url: `/contractDocuments/${contract._id}/${contract.documents[i].name}/send`,
              body: {}
            });
          }
        }
        _message.success(`Documents envoyés`);
        navigate(-1);
      } catch (e) {
        if (e.response) message(e.response.status);
      }
    }
  };

  const checkIfdocAreSent = (required) => {
    for (let i = 0; i < contract.documents.length; i += 1) {
      if (contract.documents[i].required === required) {
        if (
          contract.documents[i].status !== 'VALID' &&
          contract.documents[i].status !== 'SENT'
        )
          return true;
      }
    }
    return false;
  };

  const getPopupText = (required) => {
    let isOk = false;
    for (let i = 0; i < contract.documents.length; i += 1) {
      if (contract.documents[i].required === required) {
        if (
          contract.documents[i].status === 'SAVED' &&
          contract.documents[i].name !== 'other'
        ) {
          isOk = true;
          break;
        }
      }
    }
    if (!isOk) {
      return `Vous n'avez enregistré aucun document.`;
    }
    const text = [];
    for (let i = 0; i < contract.documents.length; i += 1) {
      if (
        contract.documents[i].status !== 'SAVED' &&
        contract.documents[i].status !== 'SENT' &&
        contract.documents[i].status !== 'VALID' &&
        contract.documents[i].name !== 'other' &&
        contract.documents[i].required === required
      ) {
        text.push(t(`contractDocuments.${contract.documents[i].name}`));
      }
    }
    if (text.length === 1)
      return `Le ${text[0]} est manquant.\nÊtes-vous sûr(e) de vouloir envoyer quand même ?`;
    if (text.length > 1) {
      let str = 'Les documents suivants sont manquants : ';
      for (let i = 0; i < text.length; i += 1) {
        str += `${text[i]}${text.length === i + 1 ? '.' : ','}`;
      }
      return `${str}\nÊtes-vous sûr(e) de vouloir envoyer quand même ?`;
    }
    return 'Êtes-vous sûr(e) de vouloir envoyer ces documents ?';
  };

  return (
    <div>
      <PageHeader title="Pièces manquantes" />
      <ContentCustom>
        <Skeleton loading={isLoading}>
          <Descriptions label="Informations Contrat">
            <Descriptions.Item label="Numero">
              <Link
                to={
                  contract &&
                  `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${contract._id}`
                }
              >
                {contract && contract.number}
              </Link>
            </Descriptions.Item>
            <Descriptions.Item label="Client">
              <Link
                to={`${!customerView ? routes.CONTRACTS : ''}${
                  !customerView ? subRoutes.CONTRACTS.PORTFOLIO_CLIENTS : ''
                }${customerView ? outOfNavRoutes.PROFILE : ''}${
                  !customerView ? '/show/' : ''
                }${!customerView ? contract?._id : ''}`}
              >
                {contract &&
                  contract.customer &&
                  `${contract.customer?.first_name} ${contract.customer?.last_name}`}
              </Link>
            </Descriptions.Item>
            <Descriptions.Item label="Date d'effet">
              {contract && moment(contract.effective_date).format('DD-MM-YYYY')}
            </Descriptions.Item>
          </Descriptions>
          <Divider plain>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              Obligatoires
            </Typography.Title>
          </Divider>
          {(contract?.documents || [])
            .filter((doc) => doc.required && doc.name !== 'other')
            .map((doc) => (
              <>
                {doc.amendmentNumber < 1 ? <ContractIcon /> : <AmendmentIcon />}
                <MissingDocUpload
                  doc={doc}
                  contractId={contract._id}
                  setRefresh={forceRefresh}
                />
              </>
            ))}
          <div style={{ width: '100%' }}>
            {contract && contract.documents && checkIfdocAreSent(true) && (
              <Popconfirm
                title={getPopupText(true)}
                okText={t('buttons.send')}
                okButtonProps={{ type: 'primary' }}
                cancelText={t('buttons.cancel')}
                onConfirm={() => validateDocs(true)}
                icon={<WarningOutlined />}
              >
                <Button
                  type="add"
                  icon={<CheckOutlined />}
                  style={{ margin: '5px 8px 5px 8px', float: 'right' }}
                >
                  {t('buttons.validate_send')}
                </Button>
              </Popconfirm>
            )}
          </div>

          <Divider plain>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              Facultatives
            </Typography.Title>
          </Divider>
          {(contract?.documents || [])
            .filter((doc) => !doc.required)
            .map((doc) => (
              <MissingDocUpload
                doc={doc}
                contractId={contract._id}
                setRefresh={forceRefresh}
              />
            ))}
          <div style={{ width: '100%' }}>
            {contract && contract.documents && checkIfdocAreSent(false) && (
              <Popconfirm
                title={getPopupText(false)}
                okText={t('buttons.send')}
                okButtonProps={{ type: 'primary' }}
                cancelText={t('buttons.cancel')}
                onConfirm={() => validateDocs(false)}
                icon={<WarningOutlined />}
              >
                <Button
                  type="add"
                  icon={<CheckOutlined />}
                  style={{ margin: '5px 8px 5px 8px', float: 'right' }}
                >
                  {t('buttons.validate_send')}
                </Button>
              </Popconfirm>
            )}
          </div>
          <Divider plain>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              DIVERS
            </Typography.Title>
          </Divider>
          {(contract?.documents || [])
            .filter((doc) => doc.name === 'other')
            .map((doc) => (
              <MissingDocUpload
                doc={doc}
                contractId={contract._id}
                setRefresh={forceRefresh}
              />
            ))}
          <div style={{ width: '100%' }}>
            {contract && contract.documents && checkIfdocAreSent(false) && (
              <Popconfirm
                title={getPopupText(false)}
                okText={t('buttons.send')}
                okButtonProps={{ type: 'primary' }}
                cancelText={t('buttons.cancel')}
                onConfirm={() => validateDocs(false)}
                icon={<WarningOutlined />}
              >
                <Button
                  type="add"
                  icon={<CheckOutlined />}
                  style={{ margin: '5px 8px 5px 8px', float: 'right' }}
                >
                  {t('buttons.validate_send')}
                </Button>
              </Popconfirm>
            )}
          </div>
        </Skeleton>
      </ContentCustom>
    </div>
  );
};

export default ShowMissingDocuments;
